import React, { useState, useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import toast from 'react-hot-toast';
import { uploadDocuments } from '../api';
import { X, Upload, FileText, Trash2, Shield, AlertTriangle, Info, Bug, Video, Mic } from 'lucide-react';
import ClientPDFPageSelector from './ClientPDFPageSelector';
import DocumentAssessmentModal from './DocumentAssessmentModal';

// The server refuses any file over 100MB, video and audio included (backend
// middleware/upload.js MAX_UPLOAD_BYTES; every file here goes to the one
// /api/documents/upload route). Checking it on pick means nobody waits through
// a long upload only to be told no at the end.
export const MAX_FILE_MB = 100;
export const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

const DOCUMENT_CATEGORIES = [
  "Policy",
  "Process / Procedure",
  "Risk",
  "Regulatory",
  "Control",
  "Context",
  "Interview Notes",
  "Visual / Diagram",
  "Other"
];

interface SecurityError {
  filename: string;
  errors: string[];
  warnings: string[];
  securityFlags: string[];
  securityThreat?: boolean;
  reason?: string;
  guidance?: {
    message: string;
    canRetry: boolean;
    suggestions: string[];
  };
}

interface FileWithMetadata {
  file: File;
  category: string;
  description: string;
  instructions: string;
  role: string;
  error?: string;
}

interface UploadDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (uploadedDocs?: Array<{id: number, original_name: string, file_type: string}>) => void;
  loadDocuments : () => void;
  currentProject: { id: number };
}

const UploadDocumentsModal: React.FC<UploadDocumentsModalProps> = ({ isOpen, onClose, onSuccess, currentProject, loadDocuments }) => {
  const [stagedFiles, setStagedFiles] = useState<FileWithMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [securityErrors, setSecurityErrors] = useState<SecurityError[]>([]);

  // Document Assessment Modal state
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [uploadedDocsForAssessment, setUploadedDocsForAssessment] = useState<Array<{id: number, original_name: string, file_type: string}>>([]);

  // PDF Page Selector state
  const [pageSelectFile, setPageSelectFile] = useState<File | null>(null);
  const [isPageSelectorOpen, setIsPageSelectorOpen] = useState(false);
  const [pendingPdfFiles, setPendingPdfFiles] = useState<File[]>([]);

  const isMediaFile = (file: File) =>
    file.type.startsWith('video/') || file.type.startsWith('audio/');

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    setUploadProgress(0);
    setSecurityErrors([]);

    if (acceptedFiles.length > 0) {
      // Media files (video/audio) skip the PDF page selector — route directly to staged
      const mediaFiles = acceptedFiles.filter(isMediaFile);
      const nonMediaFiles = acceptedFiles.filter(f => !isMediaFile(f));

      if (mediaFiles.length > 0) {
        setStagedFiles(prev => [
          ...prev,
          ...mediaFiles.map(f => ({ file: f, category: '', description: '', role: 'primary' as const })),
        ]);
      }

      // Non-media files go through the PDF page selector as before
      if (nonMediaFiles.length > 0) {
        setPendingPdfFiles(prev => [...prev, ...nonMediaFiles]);
        if (!isPageSelectorOpen) {
          setPageSelectFile(nonMediaFiles[0]);
          setIsPageSelectorOpen(true);
        }
      }
    }

    if (fileRejections.length > 0) {
      fileRejections.forEach(rejection => {
        rejection.errors.forEach(error => {
          if (error.code === 'file-too-large') {
            toast.error(`File is too large: ${rejection.file.name}. Each file must be under ${MAX_FILE_MB}MB.`);
          } else if (error.code === 'too-many-files') {
            toast.error('You can only upload a maximum of 20 files at a time.');
          } else if (error.code === 'file-invalid-type') {
            toast.error(`Cannot upload ${rejection.file.name}. Supported: PDF, Word, Excel, PowerPoint, CSV, TXT, Images, Visio, MP4, MOV, WebM, MP3, WAV, M4A.`);
          } else {
            toast.error(`Cannot upload ${rejection.file.name}: ${error.message}`);
          }
        });
      });
    }
  }, [isPageSelectorOpen]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'application/vnd.ms-excel': ['.xls', '.xlsx'],
      'text/csv': ['.csv'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/plain': ['.txt'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
      // Visio files — browsers may send as octet-stream
      'application/vnd.ms-visio.drawing': ['.vsdx'],
      'application/vnd.visio': ['.vsd'],
      'application/octet-stream': ['.vsdx', '.vsd'],
      // Video
      'video/mp4': ['.mp4'],
      'video/webm': ['.webm'],
      'video/quicktime': ['.mov'],
      'video/x-msvideo': ['.avi'],
      // Audio
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav'],
      'audio/x-wav': ['.wav'],
      'audio/mp4': ['.m4a'],
      'audio/x-m4a': ['.m4a'],
    },
    maxFiles: 20,
    maxSize: MAX_FILE_BYTES,
  });

  const handleMetadataChange = (index: number, field: keyof Omit<FileWithMetadata, 'file'>, value: string) => {
    const newFiles = [...stagedFiles];
    newFiles[index][field] = value;
    setStagedFiles(newFiles);
  };

  const handleRemoveFile = (index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handler when pages are extracted from PDF
  const handlePagesExtracted = (extractedFile: File, selectedPages: number[], originalPageCount: number) => {
    // Add the extracted PDF to staged files
    setStagedFiles(prev => [...prev, {
      file: extractedFile,
      category: '',
      description: '', // User will manually enter description
      instructions: '',
      role: 'primary'
    }]);

    // Process next PDF in queue
    processNextPdf();
  };

  // Handler when user skips page selection (upload all pages)
  const handleSkipPageSelection = () => {
    if (pageSelectFile) {
      // Add original file to staged files
      setStagedFiles(prev => [...prev, {
        file: pageSelectFile,
        category: '',
        description: '',
        instructions: '',
        role: 'primary'
      }]);
    }

    // Process next PDF in queue
    processNextPdf();
  };

  // Process next PDF in pending queue
  const processNextPdf = () => {
    const remainingPdfs = pendingPdfFiles.filter(f => f !== pageSelectFile);
    setPendingPdfFiles(remainingPdfs);

    if (remainingPdfs.length > 0) {
      // Open selector for next PDF
      setPageSelectFile(remainingPdfs[0]);
      setIsPageSelectorOpen(true);
    } else {
      // No more PDFs, close selector
      setPageSelectFile(null);
      setIsPageSelectorOpen(false);
    }
  };

  // Close page selector without adding file
  const handleClosePageSelector = () => {
    processNextPdf();
  };

  const validateFiles = () => {
    const updatedFiles = stagedFiles.map(f => ({ ...f, error: undefined }));
    setStagedFiles(updatedFiles);
    return true;
  };

  const handleUpload = async () => {
    if (!validateFiles()) {
      return;
    }
    setLoading(true);
    setUploadProgress(0);

    const documentsMetadata = stagedFiles.map(f => ({
      description: f.description,
      instructions: f.instructions,
      category: f.category,
      role: f.role,
    }));

    const formData = new FormData();
    formData.append('documentsMetadata', JSON.stringify(documentsMetadata || []));
    formData.append('projectId', currentProject.id.toString());
    
    stagedFiles.forEach(file => {
      formData.append('documents', file.file);
    });

    try {
      const response = await uploadDocuments(formData, (progressEvent) => {
        if (progressEvent.total) {
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      });
      const failed = response.data.failures || [];
      if (failed.length > 0) {
        toast.error(`${failed[0].filename}: ${failed[0].error}`);
      } else {
        toast.success(response.data.message || 'Documents uploaded successfully!');
      }
      setTimeout(() => {
        setLoading(false);
        setStagedFiles([]); // Clear the form after upload

        // Pass uploaded documents info for assessment
        const uploadedDocs = response.data.documents || [];

        // Check if any documents are SOPs or Process Maps that might need assessment
        const assessableCategories = ['Process / Procedure', 'Process Map', 'Flowchart', 'Policy'];
        const hasAssessableDocuments = stagedFiles.some(f =>
          assessableCategories.includes(f.category)
        );

        if (hasAssessableDocuments && uploadedDocs.length > 0) {
          // Show assessment modal for structured documents
          setUploadedDocsForAssessment(uploadedDocs);
          setShowAssessmentModal(true);
        } else {
          // Skip assessment for other document types
          onSuccess(uploadedDocs);
          onClose();
        }
      }, 1500);
    } catch (error: any) {
      setLoading(false);
      const apiError = error.response?.data;
      
      // Handle security validation errors
      if (apiError?.details && Array.isArray(apiError.details)) {
        setSecurityErrors(apiError.details);
        // Don't show toast for security errors - we'll show custom UI
        return;
      }
      
      // Handle other errors
      let errorMessage = apiError?.error || 'An error occurred during upload.';
      if (apiError?.duplicates && apiError.duplicates.length > 0) {
        errorMessage = `Some files were duplicates and skipped: ${apiError.duplicates.join(', ')}`;
      }
      toast.error(errorMessage);
    }
  };

  const renderSecurityError = (securityError: SecurityError) => {
    const isVirusThreat = securityError.securityThreat || securityError.reason?.toLowerCase().includes('malware');
    const hasSecurityFlags = securityError.securityFlags && securityError.securityFlags.length > 0;
    
    return (
      <div key={securityError.filename} className={`border-2 rounded-lg p-4 mb-4 ${
        isVirusThreat ? 'border-red-500 bg-red-50' : 'border-yellow-500 bg-yellow-50'
      }`}>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            {isVirusThreat ? (
              <Shield className="w-6 h-6 text-red-600" />
            ) : hasSecurityFlags ? (
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            ) : (
              <Bug className="w-6 h-6 text-orange-600" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h4 className={`font-semibold ${
                isVirusThreat ? 'text-red-800' : 'text-yellow-800'
              }`}>
                {isVirusThreat ? '🚨 SECURITY THREAT DETECTED' : 
                 hasSecurityFlags ? '⚠️ SECURITY WARNING' : 
                 '🔍 VALIDATION FAILED'}
              </h4>
              <button
                onClick={() => setSecurityErrors(prev => prev.filter(e => e.filename !== securityError.filename))}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <p className={`font-medium mb-2 ${
              isVirusThreat ? 'text-red-700' : 'text-yellow-700'
            }`}>
              📁 {securityError.filename}
            </p>
            
            {/* Main reason/threat */}
            {securityError.reason && (
              <div className={`p-3 rounded-md mb-3 ${
                isVirusThreat ? 'bg-red-100 border border-red-200' : 'bg-yellow-100 border border-yellow-200'
              }`}>
                <p className={`font-medium ${
                  isVirusThreat ? 'text-red-800' : 'text-yellow-800'
                }`}>
                  {securityError.reason}
                </p>
              </div>
            )}
            
            {/* Security flags */}
            {hasSecurityFlags && (
              <div className="mb-3">
                <p className="font-medium text-gray-700 mb-1">Security Issues:</p>
                <div className="flex flex-wrap gap-2">
                  {securityError.securityFlags.map((flag, index) => (
                    <span key={index} className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-sm font-medium">
                      {flag.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Errors and warnings */}
            {securityError.errors && securityError.errors.length > 0 && (
              <div className="mb-3">
                <p className="font-medium text-red-700 mb-1">Issues Found:</p>
                <ul className="list-disc list-inside space-y-1">
                  {securityError.errors.map((error, index) => (
                    <li key={index} className="text-red-600 text-sm">{error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Guidance */}
            {securityError.guidance && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="flex items-start space-x-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-blue-800 font-medium mb-2">What to do:</p>
                    {securityError.guidance.suggestions && securityError.guidance.suggestions.length > 0 && (
                      <ul className="list-disc list-inside space-y-1">
                        {securityError.guidance.suggestions.map((suggestion, index) => (
                          <li key={index} className="text-blue-700 text-sm">{suggestion}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-600">
                {isVirusThreat ? (
                  <span className="text-red-600 font-medium">🛡️ File blocked for security</span>
                ) : (
                  <span>📋 Review and fix the issues above</span>
                )}
              </div>
              {securityError.guidance?.canRetry && (
                <span className="text-sm text-blue-600 font-medium">
                  ✅ You can try uploading a corrected file
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Upload Documents</h2>
          <button onClick={onClose}><X /></button>
        </div>

        <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-6 cursor-pointer transition-all ${isDragActive ? 'border-electric-blue bg-light-aqua' : 'border-gray-300 hover:border-electric-blue hover:bg-gray-50'}`}>
          <input {...getInputProps()} />
          <div className="text-center">
            <Upload className="mx-auto h-10 w-10 text-gray-400" />
            <p className="text-xs text-gray-500 mt-2">
              Supports PDF, Word, Excel, PowerPoint, CSV, Text, Images, Visio — and Video/Audio (MP4, WebM, MOV, MP3, WAV, M4A)
              <br />
              Each file up to {MAX_FILE_MB}MB, video and audio included • Images for AI visual analysis • Max 20 files
            </p>
          </div>
        </div>

        {/* Security Errors Display */}
        {securityErrors.length > 0 && (
          <div className="mt-4 max-h-60 overflow-y-auto">
            {securityErrors.map(renderSecurityError)}
          </div>
        )}

        <div className="flex-grow overflow-y-auto mt-4 pr-2 space-y-3">
          {stagedFiles.map((item, index) => (
            <div key={index} className={`p-3 border rounded-lg ${item.error ? 'border-red-500' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {item.file.type.startsWith('video/') ? (
                    <Video className="h-5 w-5 text-purple-500" />
                  ) : item.file.type.startsWith('audio/') ? (
                    <Mic className="h-5 w-5 text-blue-500" />
                  ) : (
                    <FileText className="h-5 w-5" />
                  )}
                  <span className="font-medium">{item.file.name}</span>
                  {(item.file.type.startsWith('video/') || item.file.type.startsWith('audio/')) && (
                    <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                      {item.file.type.startsWith('video/') ? 'Video — transcript + OCR' : 'Audio — transcript'}
                    </span>
                  )}
                </div>
                <button onClick={() => handleRemoveFile(index)} className="text-red-500 hover:text-red-700"><Trash2 /></button>
              </div>
              <div className="space-y-2 mt-2">
                <select value={item.category} onChange={e => handleMetadataChange(index, 'category', e.target.value)} className="w-full p-2 border rounded bg-gray-50 focus:bg-white focus:ring-1 focus:ring-electric-blue">
                  <option value="" className='text-gray-400 italic'>Select a Category (Optional)</option>
                  {DOCUMENT_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
                <select value={item.role} onChange={e => handleMetadataChange(index, 'role', e.target.value)} className="w-full p-2 border rounded bg-gray-50 focus:bg-white focus:ring-1 focus:ring-electric-blue">
                  <option value="primary">Primary — defines process steps</option>
                  <option value="supporting">Supporting — adds detail only</option>
                  <option value="reference">Reference — adds detail only</option>
                </select>
                <textarea placeholder="Description (Optional)..." value={item.description} onChange={e => handleMetadataChange(index, 'description', e.target.value)} className="w-full p-2 border rounded bg-gray-50 focus:bg-white focus:ring-1 focus:ring-electric-blue" rows={2}></textarea>
                <textarea placeholder="Analysis instructions (Optional)..." value={item.instructions} onChange={e => handleMetadataChange(index, 'instructions', e.target.value)} className="w-full p-2 border rounded bg-gray-50 focus:bg-white focus:ring-1 focus:ring-electric-blue" rows={2}></textarea>
              </div>
              {(item.file.type.startsWith('video/') || item.file.type.startsWith('audio/')) && (
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Processing will extract a timestamped transcript + OCR. This may take 1–3 minutes for longer recordings.
                </p>
              )}
              {item.error && <p className="text-red-500 text-xs mt-1">{item.error}</p>}
            </div>
          ))}
        </div>

        {loading && (
          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-4">
            <div className="bg-electric-blue h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleUpload}
            disabled={loading || pendingPdfFiles.length > 0 || stagedFiles.length === 0}
            className={`px-4 py-2 rounded-lg text-white transition-all ${
              stagedFiles.length === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-electric-blue hover:bg-blue-600 disabled:opacity-50'
            }`}
          >
            {loading ? 'Uploading...' : pendingPdfFiles.length > 0 ? 'Select PDF Pages First...' : `Upload ${stagedFiles.length} File(s)`}
          </button>
        </div>
      </div>

      {/* PDF Page Selector Modal */}
      {pageSelectFile && (
        <ClientPDFPageSelector
          isOpen={isPageSelectorOpen}
          onClose={handleClosePageSelector}
          file={pageSelectFile}
          onPagesExtracted={handlePagesExtracted}
          onSkip={handleSkipPageSelection}
        />
      )}

      {/* Document Assessment Modal */}
      <DocumentAssessmentModal
        isOpen={showAssessmentModal}
        onClose={() => {
          setShowAssessmentModal(false);
          onSuccess(uploadedDocsForAssessment);
          onClose();
        }}
        uploadedDocuments={uploadedDocsForAssessment}
        onComplete={() => {
          setShowAssessmentModal(false);
          loadDocuments(); // Refresh documents list
          onSuccess(uploadedDocsForAssessment);
          onClose();
        }}
      />
    </div>
  );
};

export default UploadDocumentsModal; 